import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import supabase from 'lib/supabase-browser'
import { ContentPackType, Prompt } from 'lib/types/generated-types'
import { toast } from 'react-hot-toast'
import { useAuth } from 'util/auth'
import { useUpdateSubmission } from './submissions'

export function useContentPackTypes({ projectId }) {
  return useQuery(
    ['content_pack_types', { projectId }],
    async () => {
      const { data, error } = await supabase
        .from('content_pack_types')
        .select('*')
        .or(`project_id.eq.${projectId},project_id.is.null`)
        .not('archived', 'is', true)
      if (error) throw error

      return data
    },
    {
      enabled: !!projectId,
      keepPreviousData: true,
    }
  )
}

export function useContentPackTypesWithPrompts({ projectId }) {
  return useQuery(
    ['content_pack_types_with_prompts', { projectId }],
    async () => {
      const { data, error } = await supabase
        .from('content_pack_types')
        .select('*, prompts(*)')
        .or(`project_id.eq.${projectId},project_id.is.null`)
        .not('archived', 'is', true)
      if (error) throw error
      return data
    },
    {
      enabled: !!projectId,
      keepPreviousData: true,
    }
  )
}

export type ContentPackTypesWithPrompts = ReturnType<
  typeof useContentPackTypesWithPrompts
>['data']

export type ContentPackTypeWithPrompts =
  NonNullable<ContentPackTypesWithPrompts>[number]

export function useContentPackType({ typeId }) {
  return useQuery(
    ['content_pack_type', { typeId }],
    async () => {
      const { data, error } = await supabase
        .from('content_pack_types')
        .select('*')
        .eq('id', typeId)
        .not('archived', 'is', true)
        .single()
      if (error) throw error
      return data
    },
    {
      enabled: !!typeId,
      keepPreviousData: true,
    }
  )
}

export function useSubmissionContentPackType({ submissionId }) {
  return useQuery(
    ['submission_content_pack_type', { submissionId }],
    async () => {
      const { data, error } = await supabase
        .from('submissions')
        .select('content_pack_types(id, name, description)')
        .eq('id', submissionId)
        .single()

      if (error) throw error
      return data?.content_pack_types
    },
    {
      enabled: !!submissionId,
    }
  )
}

export const useDeleteContentPackType = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async ({ type_id }: { type_id: number }) => {
      const { data: contentPackType, error } = await supabase
        .from('content_pack_types')
        .update({ archived: true })
        .eq('id', type_id)
        .select('*')
        .single()

      if (error || !contentPackType || !contentPackType.project_id) {
        toast.error('Failed to delete content pack type')
        throw error
      }

      const { data: prompts, error: promptsError } = await supabase
        .from('prompts')
        .update({ content_pack_type_id: null })
        .eq('content_pack_type_id', type_id)

      if (promptsError) {
        toast.error('Failed to assign prompts to base content pack type')
        throw promptsError
      }

      const { data: submissions, error: submissionsError } = await supabase
        .from('submissions')
        .update({ content_pack_type_id: null })
        .eq('content_pack_type_id', type_id)

      toast.success('Content pack type deleted')

      return contentPackType
    },
    {
      onSuccess: async (data) => {
        const project_id = data?.project_id
        queryClient.invalidateQueries(['content_pack_types'])
        queryClient.invalidateQueries(['content_pack_type'])
        queryClient.invalidateQueries(['prompts'])
      },
    }
  )
}

export const useUpdateContentPackType = () => {
  const queryClient = useQueryClient()

  return useMutation(
    async ({
      project_id,
      type_id,
      name,
      description,
    }: {
      project_id: number
      type_id: number
      name: string
      description: string
    }) => {
      const { data, error } = await supabase
        .from('content_pack_types')
        .update({ name, description })
        .eq('id', type_id)
        .eq('project_id', project_id)
        .select('*')
        .single()
      if (error) throw error

      return data
    },
    {
      onSuccess: async (data) => {
        queryClient.invalidateQueries(['content_pack_type'])
        queryClient.invalidateQueries(['content_pack_types'])
        queryClient.invalidateQueries(['prompts'])
      },
    }
  )
}

export const useCreateContentPackType = () => {
  const queryClient = useQueryClient()
  return useMutation<
    ContentPackType,
    Error,
    {
      project_id: number
      name: string
      description?: string
      prompts?: { prompt: Partial<Prompt> }[]
      created_by: string
    }
  >(
    async ({ project_id, name, description, prompts, created_by }) => {
      // Create content pack type
      const { data: contentPackType, error } = await supabase
        .from('content_pack_types')
        .insert({ project_id, name, description, created_by })
        .select('*')
        .single()
      if (error) throw error

      // Create prompts associated with the new content pack type
      if (prompts && prompts.length > 0) {
        const { data: newPrompts, error: promptsError } = await supabase
          .from('prompts')
          .insert(
            prompts.map((prompt) => ({
              ...prompt.prompt,
              name: prompt.prompt.name || '',
              prompt_text: prompt.prompt.prompt_text || '',
              content_pack_type_id: contentPackType.id,
              created_by: created_by,
              project_id: project_id,
            }))
          )
        if (promptsError) throw promptsError
      }

      return contentPackType
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['content_pack_types'])
        queryClient.invalidateQueries(['content_pack_type'])
        queryClient.invalidateQueries(['prompts'])
      },
    }
  )
}
